import { useEffect, useState } from 'react';
import './PlansPage.css';
import Loader from '../../components/Loader/Loader.jsx';
import { listPlans, updatePlan, toggleVisibility, syncNow, uploadPlanPhoto, deletePlanPhoto } from '../../api/plansAPI/plansAPI.js';
import Pagination from '../../components/Pagination/Pagination.jsx';
import useLang from '../../hooks/use-lang/useLang.js';

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 6;

const STATUS_FILTERS = ['all', 'active', 'hidden'];

function PlansPage({ onNavigate, onLogout }) {
  const { t } = useLang();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', price: '', description: '', descriptionEn: '', isActive: true });
  const [editInFlight, setEditInFlight] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [detailsPlan, setDetailsPlan] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [page, setPage] = useState(1);

  function loadPlans() {
    setLoading(true);
    return listPlans({ search, status: statusFilter })
      .then((data) => {
        setPlans(data);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    const handle = setTimeout(loadPlans, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const totalPages = Math.ceil(plans.length / PAGE_SIZE);
  const pagedPlans = plans.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  async function handleSync() {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await syncNow();
      setSyncResult(result);
      await loadPlans();
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  }

  async function handleToggleVisibility(plan) {
    try {
      const result = await toggleVisibility(plan.id);
      setPlans((prev) =>
        prev.map((p) =>
          p.id === plan.id
            ? { ...p, isActive: result.is_active, status: result.is_active ? 'active' : 'hidden' }
            : p
        )
      );
    } catch (err) {
      setError(err.message);
    }
  }

  function requestEdit(plan) {
    setEditingPlan(plan);
    setEditForm({
      name: plan.name,
      price: plan.price ? plan.price.toString() : '',
      description: plan.description,
      descriptionEn: plan.descriptionEn || '',
      isActive: plan.isActive,
    });
  }

  function cancelEdit() {
    setEditingPlan(null);
    setEditInFlight(false);
    setPhotoUploading(false);
  }

  function updatePlanPhotos(updater) {
    setEditingPlan((prev) => ({ ...prev, photos: updater(prev.photos || []) }));
    setPlans((prev) =>
      prev.map((p) => (p.id === editingPlan.id ? { ...p, photos: updater(p.photos || []) } : p))
    );
  }

  async function handlePhotoSelect(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !editingPlan) return;

    setPhotoUploading(true);
    try {
      const base64 = await fileToBase64(file);
      const photo = await uploadPlanPhoto(editingPlan.id, base64, file.type);
      updatePlanPhotos((prev) => [...prev, photo]);
    } catch (err) {
      setError(err.message);
    } finally {
      setPhotoUploading(false);
    }
  }

  async function handleDeletePhoto(photoId) {
    try {
      await deletePlanPhoto(editingPlan.id, photoId);
      updatePlanPhotos((prev) => prev.filter((ph) => ph.id !== photoId));
    } catch (err) {
      setError(err.message);
    }
  }

  async function confirmEdit() {
    if (!editingPlan) return;
    setEditInFlight(true);
    try {
      const updated = await updatePlan(editingPlan.id, {
        name: editForm.name,
        price: parseFloat(editForm.price) || 0,
        description: editForm.description,
        descriptionEn: editForm.descriptionEn,
        isActive: editForm.isActive,
      });
      setPlans((prev) => prev.map((p) => (p.id === editingPlan.id ? updated : p)));
      setEditingPlan(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setEditInFlight(false);
    }
  }

  return (
    <div className="plans__page">
      <div className="page__container">
        <div className="plans__page__header">
          <div className="plans__filters">
            {STATUS_FILTERS.map((value) => (
              <button
                key={value}
                type="button"
                className={statusFilter === value ? 'plans__filter plans__filter--active' : 'plans__filter'}
                onClick={() => setStatusFilter(value)}
              >
                {t(`common.${value}`, value)}
              </button>
            ))}
          </div>
          <div className="plans__header-right">
            <input
              className="plans__search"
              type="text"
              placeholder={t('plans.searchPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label={t('plans.searchAria', 'Search plans')}
            />
            <button
              className="plans__sync-btn"
              type="button"
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? t('plans.syncing') : t('plans.syncNow')}
            </button>
          </div>
        </div>

        {syncResult && (
          <p className="plans__sync-result">
            {t('plans.syncResult')
              .replace('{synced}', syncResult.synced)
              .replace('{new}', syncResult.new)
              .replace('{skipped}', syncResult.skipped)
              .replace('{deactivated}', syncResult.deactivated)}
          </p>
        )}

        {loading ? (
          <Loader text={t('plans.loading')} />
        ) : (
        <div className="plansPage__card__container">
          {!loading && error && <p className="plans__empty">{t('common.error')}: {error}</p>}
          {!loading && !error && plans.length === 0 && (
            <p className="plans__empty">{t('plans.empty')}</p>
          )}
          {!loading && !error && pagedPlans.map((plan) => (
            <div key={plan.id} className={plan.status === 'hidden' ? 'plans_card plans_card--hidden' : 'plans_card'}>
              <div className="plan__head">
                <div className="plan__head-left">
                  <div className="plan__avatar">
                    {plan.photos?.[0]?.url && (
                      <img className="plan__avatar-img" src={plan.photos[0].url} alt={plan.name} />
                    )}
                  </div>
                  <div className="plan__head-texts">
                    <span className="plan__name">{plan.name}</span>
                    <span className="plan__provider-id">{plan.providerPlanId}</span>
                  </div>
                </div>
                <span className={`plan__status-badge plan__status-badge--${plan.status}`}>{t(`common.${plan.status}`, plan.status)}</span>
              </div>

              <div className="plan__specs">
                <div className="plan__spec">
                  <span className="plan__spec-label">CPU</span>
                  <span className="plan__spec-value">{plan.cpu} vCPU</span>
                </div>
                <div className="plan__spec">
                  <span className="plan__spec-label">RAM</span>
                  <span className="plan__spec-value">{plan.ram} GB</span>
                </div>
                <div className="plan__spec">
                  <span className="plan__spec-label">{t('plans.disk')}</span>
                  <span className="plan__spec-value">{plan.storage} GB</span>
                </div>
                <div className="plan__spec">
                  <span className="plan__spec-label">{t('plans.arch')}</span>
                  <span className="plan__spec-value">{plan.architecture}</span>
                </div>
              </div>

              <div className="plan__pricing">
                <div className="plan__price-row">
                  <span className="plan__price-label">{t('common.provider')}</span>
                  <span className="plan__price-value">{`$${plan.providerPrice.toFixed(2)}`}</span>
                </div>
                <div className="plan__price-row">
                  <span className="plan__price-label">{t('plans.yourPrice')}</span>
                  <span className="plan__price-value">{plan.price > 0 ? `$${plan.price.toFixed(2)}` : t('common.notSpecified')}</span>
                </div>
                <div className="plan__price-row">
                  <span className="plan__price-label">{t('plans.margin')}</span>
                  <span className={`plan__margin plan__margin--${plan.marginLevel}`}>
                    {plan.marginLevel === 'none'
                      ? t('plans.marginNone')
                      : `${plan.margin >= 0 ? '+' : ''}$${plan.margin.toFixed(2)}${plan.marginPercent !== null ? ` · ${plan.marginPercent.toFixed(0)}%` : ''}`}
                  </span>
                </div>
              </div>

              <div className="plan__meta">
                <span className="plan__meta-item">{t('plans.locations').replace('{n}', plan.locationsAvailable)}</span>
                <span className="plan__meta-item">{t('plans.lastSync').replace('{d}', plan.lastSync)}</span>
              </div>

              <div className="plans__controller__container">
                <button className="plans__btn plans__btn--edit" type="button" onClick={() => requestEdit(plan)}>{t('common.edit')}</button>
                <button className="plans__btn plans__btn--hide" type="button" onClick={() => handleToggleVisibility(plan)}>
                  {plan.status === 'active' ? t('common.hide') : t('common.show')}
                </button>
              </div>
            </div>
          ))}
        </div>
        )}

        {!loading && !error && (
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        )}
      </div>

      {editingPlan && (
        <div className="plan-modal-overlay" onClick={cancelEdit}>
          <div className="plan-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="plan-modal-title">{t('plans.editTitle')}</h3>
            <div className="plan-modal__provider-info">
              <span className="plan-modal__provider-id">{editingPlan.providerPlanId}</span>
              <span className="plan-modal__provider-spec">
                {editingPlan.cpu} vCPU · {editingPlan.ram} GB · {editingPlan.storage} GB · {editingPlan.architecture}
              </span>
              <span className="plan-modal__provider-cost">{t('plans.providerCost').replace('{v}', editingPlan.providerPrice.toFixed(2))}</span>
            </div>
            <div className="plan-modal-fields">
              <label className="plan-modal-field">
                <span className="plan-modal-field-label">{t('common.name')}</span>
                <input
                  className="plan-modal-input"
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder={t('plans.namePlaceholder')}
                />
              </label>
              <label className="plan-modal-field">
                <span className="plan-modal-field-label">{t('plans.priceForClient')}</span>
                <input
                  className="plan-modal-input"
                  type="text"
                  inputMode="decimal"
                  value={editForm.price}
                  onChange={(e) => setEditForm((f) => ({ ...f, price: e.target.value }))}
                  placeholder="0.00"
                />
              </label>
              <label className="plan-modal-field">
                <span className="plan-modal-field-label">{t('plans.description')} (UA)</span>
                <input
                  className="plan-modal-input"
                  type="text"
                  value={editForm.description}
                  onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder={t('plans.descPlaceholder')}
                />
              </label>
              <label className="plan-modal-field">
                <span className="plan-modal-field-label">{t('plans.descriptionEn', 'Description (EN)')}</span>
                <input
                  className="plan-modal-input"
                  type="text"
                  value={editForm.descriptionEn}
                  onChange={(e) => setEditForm((f) => ({ ...f, descriptionEn: e.target.value }))}
                  placeholder={t('plans.descEnPlaceholder', 'English description')}
                />
              </label>
              <div className="plan-modal-field">
                <span className="plan-modal-field-label">{t('plans.photos')}</span>
                <div className="plan-modal__photos">
                  {(editingPlan.photos || []).map((photo) => (
                    <div key={photo.id} className="plan-modal__photo">
                      <img className="plan-modal__photo-img" src={photo.url} alt="" />
                      <button
                        className="plan-modal__photo-del"
                        type="button"
                        onClick={() => handleDeletePhoto(photo.id)}
                        aria-label={t('plans.deletePhoto', 'Delete photo')}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <label className="plan-modal__photo-add">
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handlePhotoSelect}
                      disabled={photoUploading}
                    />
                    {photoUploading ? '…' : '+'}
                  </label>
                </div>
              </div>
              <button
                type="button"
                className={editForm.isActive ? 'plan-modal__toggle plan-modal__toggle--on' : 'plan-modal__toggle'}
                onClick={() => setEditForm((f) => ({ ...f, isActive: !f.isActive }))}
                aria-pressed={editForm.isActive}
              >
                {editForm.isActive ? t('plans.showInCatalog') : t('plans.hideFromCatalog')}
              </button>
            </div>
            <div className="plan-modal-actions">
              <button className="plans__btn plans__btn--delete" type="button" onClick={cancelEdit} disabled={editInFlight}>{t('common.cancel')}</button>
              <button className="plans__btn plans__btn--edit" type="button" onClick={confirmEdit} disabled={editInFlight}>{editInFlight ? t('common.saving') : t('common.save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlansPage;
